/**
 * Linda AI Tool Definitions
 *
 * 12 tools in OpenAI function-calling format for the LiteLLM gateway.
 * Schemas ported from the deleted Next.js API route (git e2751bd6).
 *
 * Categories:
 * - Navigation (4): navigate, select_product/group/material
 * - Filters (3): filter_products/groups/materials
 * - Lookups (5): in linda-lookup-tools.ts
 *
 * Editor tools (add_item_to_editor, move_item, etc.) are intentionally
 * dropped — the dashboard is a catalog manager, not a floor plan editor.
 */

import type {Tool} from '@/lib/litellm-client';
import {LOOKUP_TOOLS} from '@/lib/linda-lookup-tools';

export {LOOKUP_TOOLS};

// ---------------------------------------------------------------------------
// Navigation tools — returned as actions[] for useChatActions
// ---------------------------------------------------------------------------

export const NAVIGATION_TOOLS: Tool[] = [
    {
        type: 'function',
        function: {
            name: 'navigate',
            description:
                'Navigate to a different page in ModelBank. Use this when the user wants to go to a specific section.',
            parameters: {
                type: 'object',
                properties: {
                    destination: {
                        type: 'string',
                        enum: [
                            'dashboard',
                            'products',
                            'groups',
                            'materials',
                            'modular-systems',
                            'linda',
                        ],
                        description: 'The page to navigate to',
                    },
                    message: {
                        type: 'string',
                        description:
                            'A friendly message to show the user about this navigation',
                    },
                },
                required: ['destination', 'message'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'select_product',
            description:
                'Open a specific product detail page. Use when user mentions a specific product by name or wants to view details. IMPORTANT: Always use lookup_product first to get the product ID.',
            parameters: {
                type: 'object',
                properties: {
                    productId: {
                        type: 'string',
                        description:
                            'The product ID (from lookup_product results - use the "id" field, NOT the "sku" field)',
                    },
                    page: {
                        type: 'string',
                        enum: [
                            'product-details',
                            '3d-model',
                            'media',
                            'renders',
                            'ai-lifestyle',
                            'configurator',
                            'external-viewer',
                            'linda-cards',
                        ],
                        description:
                            'Optional: specific sub-page to navigate to (defaults to main product page if not specified)',
                    },
                    message: {
                        type: 'string',
                        description: 'A friendly message to show the user',
                    },
                },
                required: ['productId', 'message'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'select_group',
            description:
                'Open a specific group detail page. Use when user mentions a specific group by name. IMPORTANT: Always use lookup_group first to get the group ID.',
            parameters: {
                type: 'object',
                properties: {
                    groupId: {
                        type: 'string',
                        description:
                            'The group ID (from lookup_group results - use the "id" field)',
                    },
                    page: {
                        type: 'string',
                        enum: [
                            'group-details',
                            'media',
                            'renders',
                            'ai-lifestyle',
                            'external-viewer',
                        ],
                        description:
                            'Optional: specific sub-page to navigate to (defaults to main group page if not specified)',
                    },
                    message: {
                        type: 'string',
                        description: 'A friendly message to show the user',
                    },
                },
                required: ['groupId', 'message'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'select_material',
            description:
                'Open a specific material detail page. Use when user mentions a specific material by name or wants to view details. IMPORTANT: Always use lookup_material first to get the material ID.',
            parameters: {
                type: 'object',
                properties: {
                    materialId: {
                        type: 'string',
                        description:
                            'The material ID (from lookup_material results - use the "id" field)',
                    },
                    page: {
                        type: 'string',
                        enum: [
                            'material-details',
                            'texture-maps',
                            'dynamic-models',
                        ],
                        description:
                            'Optional: specific sub-page to navigate to (defaults to main material page if not specified)',
                    },
                    message: {
                        type: 'string',
                        description: 'A friendly message to show the user',
                    },
                },
                required: ['materialId', 'message'],
            },
        },
    },
];

// ---------------------------------------------------------------------------
// Filter tools — returned as actions[] for useChatActions
// ---------------------------------------------------------------------------

export const FILTER_TOOLS: Tool[] = [
    {
        type: 'function',
        function: {
            name: 'filter_products',
            description:
                'Filter products by status, category, supplier, or sort them. Use when user wants to filter or sort products.',
            parameters: {
                type: 'object',
                properties: {
                    status: {
                        type: 'string',
                        enum: ['approved', 'pending', 'rejected', ''],
                        description:
                            'Filter by product status (empty string for all)',
                    },
                    category: {
                        type: 'string',
                        description: 'Filter by category name',
                    },
                    subcategory: {
                        type: 'string',
                        description: 'Filter by subcategory name',
                    },
                    sortBy: {
                        type: 'string',
                        enum: ['created_at', 'name', 'sku', 'id'],
                        description: 'Field to sort by',
                    },
                    sortOrder: {
                        type: 'string',
                        enum: ['asc', 'desc'],
                        description: 'Sort order',
                    },
                    message: {
                        type: 'string',
                        description: 'A friendly message to show the user',
                    },
                },
                required: ['message'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'filter_groups',
            description:
                'Filter groups by state or user. Use when user wants to filter groups.',
            parameters: {
                type: 'object',
                properties: {
                    state: {
                        type: 'string',
                        enum: ['hidden', 'shared', ''],
                        description:
                            'Filter by group state (empty string for all)',
                    },
                    sortOrder: {
                        type: 'string',
                        enum: ['asc', 'desc'],
                        description: 'Sort order',
                    },
                    message: {
                        type: 'string',
                        description: 'A friendly message to show the user',
                    },
                },
                required: ['message'],
            },
        },
    },
    {
        type: 'function',
        function: {
            name: 'filter_materials',
            description:
                'Filter materials by kind, source type, or sort them. Use when user wants to filter or sort materials.',
            parameters: {
                type: 'object',
                properties: {
                    kind: {
                        type: 'string',
                        description:
                            'Filter by material kind/type (e.g., fabric, wood, metal)',
                    },
                    source: {
                        type: 'string',
                        enum: ['static', 'dynamic', ''],
                        description:
                            'Filter by source type (static, dynamic, or empty string for all)',
                    },
                    sortBy: {
                        type: 'string',
                        enum: ['name', 'sku', 'created_at', 'updated_at'],
                        description: 'Field to sort by',
                    },
                    sortOrder: {
                        type: 'string',
                        enum: ['asc', 'desc'],
                        description: 'Sort order',
                    },
                    message: {
                        type: 'string',
                        description: 'A friendly message to show the user',
                    },
                },
                required: ['message'],
            },
        },
    },
];

// ---------------------------------------------------------------------------
// Combined exports + helpers
// ---------------------------------------------------------------------------

/** All 12 tools for the LiteLLM request */
export const LINDA_TOOLS: Tool[] = [
    ...NAVIGATION_TOOLS,
    ...FILTER_TOOLS,
    ...LOOKUP_TOOLS,
];

const LOOKUP_TOOL_NAMES = new Set([
    'lookup_product',
    'lookup_group',
    'lookup_material',
    'ai_search_products',
    'generate_renders',
]);

/** Is this a lookup tool? (executed client-side, results fed back to Claude) */
export function isLookupTool(name: string): boolean {
    return LOOKUP_TOOL_NAMES.has(name);
}

/**
 * Check if a message is requesting render generation.
 * Used to force tool_choice when user asks for renders.
 */
export function isRenderRequest(message: string): boolean {
    return /\b(create|generate|make|start|render|silo|360|spin)\b.*\b(render|silo|spin|360)\b/i.test(
        message.toLowerCase()
    );
}
